import {issuers} from '@apps-common/auth';
import {setBaseUrl as setAuthUtilsBaseURL} from '@apps-common/auth-utils';

// This is also copied over into e2e/settings.js
export const API_TYPE = import.meta.env.VITE_API_TYPE ?? 'next';

// API --------------------------------------------------------------------

// This is also copied over into e2e/settings.js
export const API_BASE_URL = {
  live: 'https://api.planet.com',
  next: 'https://api.staging.planet-labs.com',
  edition: import.meta.env.VITE_API_BASE_URL,
  local: import.meta.env.VITE_API_BASE_URL,
}[API_TYPE];

setAuthUtilsBaseURL(API_BASE_URL);

// Granero (Features/OGC API) host. Defaults to API_BASE_URL, but can be
// pointed elsewhere via VITE_GRANERO_TARGET — useful for running Account
// against a local pda-admin (which does not serve /features) while still
// reaching a real Granero deployment (e.g. https://api.staging.planet-labs.com).
export const GRANERO_BASE_URL =
  import.meta.env.VITE_GRANERO_TARGET || API_BASE_URL;

export const PDA_ADMIN_V0_BASE_PATH = {
  live: 'account/v0',
  next: 'account/v0',
  edition: 'v0',
  local: 'v0',
}[API_TYPE];

// This is also copied over into e2e/settings.js
export const PDA_ADMIN_V1_BASE_PATH = {
  live: 'account/v1',
  next: 'account/v1',
  edition: 'public',
  local: 'public',
}[API_TYPE];

export const REPORTS_API_BASE_PATH = 'reports/v1';

export const DATA_API_BASE_URL = {
  live: '/data/v1',
  next: 'https://platform-data-service.staging.planet-labs.com/data/v1',
  edition: import.meta.env.VITE_DATA_API_BASE_URL,
  // The Data API is a hosted Planet service (not run locally), so local dev
  // points at staging by default — same host web-admin-ng uses in dev. Without
  // this fallback the base is undefined and requests resolve against pda-admin
  // (e.g. localhost:5011/undefined/item-types → 404). Override via env if needed.
  local:
    import.meta.env.VITE_DATA_API_BASE_URL ||
    'https://platform-data-service.staging.planet-labs.com/data/v1',
}[API_TYPE];

// AdminNG ----------------------------------------------------------------

const ADMIN_NG_URL =
  import.meta.env.VITE_ADMIN_NG_URL ??
  'https://static.prod.planet-labs.com/web-admin-ng/next/main/';

export const ADMIN_NG_BASE_URL = {
  live: 'https://www.planet.com/admin/',
  next: ADMIN_NG_URL,
  edition: ADMIN_NG_URL,
  local: ADMIN_NG_URL,
}[API_TYPE];

// Authentication (Auth0) -------------------------------------------------

export const AUTH_ENV = {
  prod: {
    issuer: issuers.production,
    clientId: '3bK8E1FsOHyZJod8LmAYQa6bzogbVxD0', // prod issuer, prod client ID
  },
  prod_dev: {
    issuer: issuers.production,
    clientId: 'ZTYtGIlwaXb4w0gGSv5Ys2GgeDsI0Ilb', // prod issuer, dev client ID
  },
  staging: {
    issuer: issuers.staging,
    clientId: 'tENldp3axt0FFkRVnKegZRDkTWIsRaZx', // staging issuer, prod client ID
  },
  staging_dev: {
    issuer: issuers.staging,
    clientId: 'KETpP7ZZztMbS8GiLqbJEJ5Snz9TBsZn', // staging issuer, dev client ID
  },
}[import.meta.env.VITE_AUTH_ENV ?? 'staging_dev'];

// LaunchDarkly -----------------------------------------------------------

export const LD_CLIENTSIDE_ID = {
  prod: import.meta.env.VITE_LD_CLIENTSIDE_ID_PROD,
  staging: import.meta.env.VITE_LD_CLIENTSIDE_ID_STAGING,
  local: import.meta.env.VITE_LD_CLIENTSIDE_ID_LOCAL,
  qe: import.meta.env.VITE_LD_CLIENTSIDE_ID_QE,
}[import.meta.env.VITE_LD_ENV ?? 'local'];

// Features Manager -------------------------------------------------------

export const FEATURES_MANAGER_URL = {
  live:
    window.location.hostname === 'www.planet.com'
      ? 'https://planet.com/features/'
      : 'https://static.prod.planet-labs.com/features-manager/live/main/',
  next: 'https://static.prod.planet-labs.com/features-manager/next/main/',
  edition: 'https://static.prod.planet-labs.com/features-manager/next/main/',
  local: 'https://static.prod.planet-labs.com/features-manager/next/main/',
}[API_TYPE];

// Sentinel Hub -----------------------------------------------------------

export const SH_KEYCLOAK_STAGING = 'https://stage.sentinel-hub.com';
export const SH_KEYCLOAK_PRODUCTION = 'https://services.sentinel-hub.com';

export const SH_KEYCLOAK =
  API_TYPE === 'live' ? SH_KEYCLOAK_PRODUCTION : SH_KEYCLOAK_STAGING;

export const SH_KEYCLOAK_OAUTH = `${SH_KEYCLOAK}/auth/realms/main/protocol/openid-connect/auth`;

export const SH_TOKEN_ENDPOINT = `${SH_KEYCLOAK}/auth/realms/main/protocol/openid-connect/token?kc_idp_hint=planet-account-link`;

export const SH_CLIENT_ID = 'ceb1ef03-5c6f-42b3-88a0-67e70b4d2c5c';
export const SH_GRANT_TYPE = 'authorization_code';

// Insights Platform Apps

export const INSIGHTS_URL = 'https://insights.planet.com';
export const ACCOUNT_MANAGER_URL = `${INSIGHTS_URL}/account/`;
export const ORDERS_URL = `${INSIGHTS_URL}/data/orders/`;
export const COLLECTIONS_URL = `${INSIGHTS_URL}/data/collections`;

// IMS

export const IMS_BASE_STAGING = 'https://staging1-awseu.sentinel-hub.com/ims';
export const IMS_BASE_PRODUCTION = 'https://services.sentinel-hub.com/ims';
export const IMS_BASE_PATH =
  API_TYPE === 'live' ? IMS_BASE_PRODUCTION : IMS_BASE_STAGING;

// Labs -------------------------------------------------------------------

/**
 * Lab Definitions
 *
 * These configs allow us to control access to various experimental
 * features we're not ready to release everywhere.
 *
 * Example Config:
   {
      id: 'unique-id', <-- used for localStorage
      name: 'Name',
      permission: [roleLevels.planet_admin],
      icon: <Icon />,
    },
 */

export const labDefinitions = [];

export const labsNameSpace = '@planet/adminv3/labs/';

export const USER_MANAGEMENT_PRODUCTION =
  'https://static.prod.planet-labs.com/management/production';
export const USER_MANAGEMENT_STAGING =
  'https://static.prod.planet-labs.com/management/stage1';

export const USER_MANAGEMENT_URL =
  API_TYPE === 'live' ? USER_MANAGEMENT_PRODUCTION : USER_MANAGEMENT_STAGING;

export const SH_CREATE_CA_URL = `${USER_MANAGEMENT_URL}/#/customer-accounts`;

export const JIRA_AMENDMENT_URL =
  'https://hello.planet.com/jira/servicedesk/customer/portal/6/create/485';
